import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const PROGRESSION = [
  { step: 1, stake: 200, cum: 200, result: "LOSS", next: 400 },
  { step: 2, stake: 400, cum: 600, result: "LOSS", next: 800 },
  { step: 3, stake: 800, cum: 1400, result: "LOSS", next: 1600 },
  { step: 4, stake: 1600, cum: 3000, result: "WIN @ -110", profit: "+$1,454.55" },
];

const VARIANTS = [
  {
    name: "CLASSIC",
    desc: "Double after every loss",
    sequence: [200, 400, 800, 1600, 3200],
    bankroll_needed: 6200,
  },
  {
    name: "GRAND",
    desc: "Double + 1 base unit after every loss",
    sequence: [200, 600, 1400, 3000, 6200],
    bankroll_needed: 11400,
  },
  {
    name: "MINI",
    desc: "Cap doubling at step 4 — safer drawdown",
    sequence: [200, 400, 800, 1600, 1600],
    bankroll_needed: 4600,
  },
  {
    name: "D'ALEMBERT",
    desc: "+1 unit per loss, −1 per win",
    sequence: [200, 400, 600, 800, 1000],
    bankroll_needed: 3000,
  },
];

export default function Strategy() {
  return (
    <div data-testid="strategy-page">
      <div className="border-b border-zinc-800 pb-10 mb-10">
        <div className="overline mb-3">WORKED·EXAMPLE</div>
        <h1 className="font-display text-4xl lg:text-5xl leading-tight">
          $2,000 bankroll · $200 base · follow the line.
        </h1>
        <p className="mt-4 text-zinc-400 max-w-2xl">
          Here is exactly how to run a Martingale progression with your bankroll settings.
          Start at $200. If you lose, next bet is $400. Lose again, $800. The line
          doubles — and the moment you win, you reset to $200.
        </p>
      </div>

      {/* Setup card */}
      <div className="grid md:grid-cols-3 gap-0 border border-zinc-800 mb-12">
        {[
          { label: "BANKROLL", val: "$2,000" },
          { label: "BASE STAKE", val: "$200", tone: "green" },
          { label: "TYPICAL ODDS", val: "-110", tone: "amber" },
        ].map((s) => (
          <div
            key={s.label}
            className="p-6 border-r border-zinc-800 last:border-r-0"
            data-testid={`setup-${s.label.toLowerCase().replace(" ", "-")}`}
          >
            <div className="overline">{s.label}</div>
            <div
              className={`font-display text-4xl mt-3 ${
                s.tone === "green" ? "neon-text" : s.tone === "amber" ? "amber-text" : ""
              }`}
            >
              {s.val}
            </div>
          </div>
        ))}
      </div>

      {/* Step-by-step narrative */}
      <div className="mb-16">
        <div className="overline mb-4">THE SEQUENCE · CLASSIC MARTINGALE</div>
        <div className="space-y-4">
          {PROGRESSION.map((p, idx) => (
            <div
              key={p.step}
              className={`grid grid-cols-12 gap-4 p-5 border ${
                p.result.startsWith("WIN")
                  ? "border-[#00D4FF] bg-[#00D4FF]/5"
                  : "border-zinc-800"
              }`}
              data-testid={`progression-step-${p.step}`}
            >
              <div className="col-span-1">
                <div className="overline">STEP</div>
                <div className="font-display text-3xl mono">{p.step}</div>
              </div>
              <div className="col-span-2">
                <div className="overline">STAKE</div>
                <div className="font-display text-3xl mono text-white">
                  ${p.stake.toLocaleString()}
                </div>
              </div>
              <div className="col-span-2">
                <div className="overline">CUMULATIVE RISK</div>
                <div className="mono text-lg text-zinc-300">${p.cum.toLocaleString()}</div>
              </div>
              <div className="col-span-3">
                <div className="overline">RESULT</div>
                <div
                  className={`mono text-lg ${
                    p.result.startsWith("WIN") ? "neon-text" : "text-red-400"
                  }`}
                >
                  {p.result}
                </div>
              </div>
              <div className="col-span-4">
                <div className="overline">NEXT MOVE</div>
                <div className="mono text-sm text-zinc-300 mt-1">
                  {p.result.startsWith("WIN") ? (
                    <>
                      <span className="neon-text">Reset to $200.</span> Net profit:{" "}
                      <span className="neon-text">{p.profit}</span>
                    </>
                  ) : (
                    <>
                      Double the line →{" "}
                      <span className="amber-text">${p.next.toLocaleString()}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 border border-[#00D4FF]/30 bg-[#00D4FF]/5 p-5">
          <div className="overline neon-text mb-2">HOW IT RESOLVES</div>
          <p className="text-sm text-zinc-200 leading-relaxed">
            After 3 losses ($200 + $400 + $800 = $1,400 risked), a $1,600 winning wager
            at -110 returns +$1,454.55 profit — covering every loss and leaving you{" "}
            <span className="neon-text">+$54.55 ahead on the whole sequence</span>. Then
            reset to a fresh $200 and start again.
          </p>
        </div>
      </div>

      {/* Variant comparison */}
      <div className="mb-16">
        <div className="overline mb-4">PICK YOUR VARIANT · $200 BASE</div>
        <div className="border border-zinc-800 overflow-x-auto">
          <table className="w-full mono text-sm min-w-[720px]" data-testid="variants-table">
            <thead className="bg-zinc-900">
              <tr>
                <th className="px-4 py-3 overline text-left">VARIANT</th>
                <th className="px-4 py-3 overline text-left">DESCRIPTION</th>
                <th className="px-4 py-3 overline text-left">
                  SEQUENCE (first 5 steps)
                </th>
                <th className="px-4 py-3 overline text-right">BANKROLL NEEDED</th>
              </tr>
            </thead>
            <tbody>
              {VARIANTS.map((v) => (
                <tr key={v.name} className="border-t border-zinc-800">
                  <td className="px-4 py-4 font-display text-xl neon-text">{v.name}</td>
                  <td className="px-4 py-4 text-zinc-400 text-xs">{v.desc}</td>
                  <td className="px-4 py-4 text-white">
                    {v.sequence.map((s) => `$${s.toLocaleString()}`).join(" → ")}
                  </td>
                  <td className="px-4 py-4 text-right amber-text text-lg">
                    ${v.bankroll_needed.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Rules */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        <div className="border border-zinc-800 p-6">
          <div className="overline neon-text mb-3">RULES — WHEN YOU WIN</div>
          <ol className="space-y-2 text-sm text-zinc-300 list-decimal list-inside">
            <li>Bank the profit. Do NOT re-risk it on the next play.</li>
            <li>Reset the next wager to $200 (base stake).</li>
            <li>If you hit your daily profit target (+5% bankroll), stop for the day.</li>
          </ol>
        </div>
        <div className="border border-zinc-800 p-6">
          <div className="overline amber-text mb-3">RULES — WHEN YOU LOSE</div>
          <ol className="space-y-2 text-sm text-zinc-300 list-decimal list-inside">
            <li>Double the previous stake (Classic) — $200 → $400 → $800 → $1,600.</li>
            <li>Never exceed the book's table limit. Most US books cap at $2,500-$10,000.</li>
            <li>If you lose step 4 of a Classic run ($1,600), STOP. Re-evaluate.</li>
            <li>Total bankroll at risk in a 4-step run: $3,000. You are shown the max before you start.</li>
          </ol>
        </div>
      </div>

      {/* Reality check */}
      <div className="border border-amber-500/40 bg-amber-500/5 p-6 mb-12">
        <div className="overline amber-text mb-3">⚠ REALITY CHECK</div>
        <div className="grid md:grid-cols-3 gap-6 text-sm text-amber-200">
          <div>
            <div className="font-display text-3xl text-white mb-2">6.25%</div>
            <p className="leading-relaxed">
              Odds of losing 4 in a row on a 50/50 bet. Not rare — roughly 1 in 16
              sequences.
            </p>
          </div>
          <div>
            <div className="font-display text-3xl text-white mb-2">+$54.55</div>
            <p className="leading-relaxed">
              Profit per completed sequence at -110. That's a 2.73% return on your
              $2,000 bankroll — if you win before busting.
            </p>
          </div>
          <div>
            <div className="font-display text-3xl text-white mb-2">-$3,000</div>
            <p className="leading-relaxed">
              Loss on a 4-deep bust. One bad sequence wipes 55 winning sequences.
              Progression shifts variance — it does not create edge.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Link
          to="/app/calculator"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#00D4FF] text-black font-mono uppercase tracking-widest text-xs hover:brightness-110"
          data-testid="strategy-to-calc"
        >
          Model your own numbers <ArrowRight size={14} strokeWidth={1.5} />
        </Link>
        <Link
          to="/app/tracker"
          className="inline-flex items-center gap-2 px-6 py-3 border border-zinc-800 font-mono uppercase tracking-widest text-xs hover:border-[#00D4FF] hover:text-[#00D4FF]"
          data-testid="strategy-to-tracker"
        >
          Open the tracker
        </Link>
      </div>
    </div>
  );
}
